import * as React from "react"
import { Container, Row, Col, Spinner, Form } from "react-bootstrap"
import { defaults } from "react-chartjs-2"
import "chartjs-plugin-datalabels"
import { ChartOptions } from "chart.js"
import { Subject, Subscription } from "rxjs"
import { PoolStatisticsDetailsByProgram } from "../../../api/Randoms/PoolStatisticsDetailsGet"
import PoolStatisticsDetails from "../../../api/Randoms/RandomsModel/PoolStatisticsDetails"
import { ExcusedReport, ExcusedReportGenerate } from "../../../api/Reports/WorkforceReport"
import FormatError from "../../../formatters/FormatError"
import { toast } from "react-toastify"
import printPdf from "../../../utils/printPdf"
import PoolProgramYear from "../../../api/Randoms/RandomsModel/PoolProgramYear"
import PoolSelectionHistoryRow from "../../../api/Randoms/RandomsModel/PoolSelectionHistoryRow"
import { GetPoolPrograms } from "../../../api/Randoms/PoolSelectionHistoryGet"
import SelectionHistoryGrid from "./SelectionHistoryGrid"

interface PoolHistoryProps {
  poolId: number
  poolName: string
  isConsortium: boolean
  currentCompany: number
  allowExcusedReport: boolean
}

interface PoolHistoryState {
  poolProgramsLoading: boolean
  poolProgramsYear: PoolProgramYear[],
  selectedPoolProgramId: number,
  selectedPoolYear: number
  statisticsLoading: boolean
  statistics?: PoolStatisticsDetails
  historyLoading: boolean
  selectionHistory: PoolSelectionHistoryRow[]

}

const options: ChartOptions = {
  maintainAspectRatio: false,
  layout: {
    padding: {
      right: 50
    }
  },
  legend: {
    position: 'bottom',
    labels: {
      usePointStyle: true,
      boxWidth: 10,
      fontSize: 16
    }
  },
  plugins: {
    datalabels: {
      color: '#000',
      display: true
    }
  },
  scales: {
    yAxes: [{ ticks: { fontSize: 16 } }],
    xAxes: [{ ticks: { min: 0 } }]
  }
}

defaults.global.defaultFontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"'
defaults.global.defaultFontColor = '#212529'

export default class PoolHistory extends React.PureComponent<PoolHistoryProps, PoolHistoryState> {
  state: PoolHistoryState = {

    poolProgramsLoading: false,
    poolProgramsYear: [],
    selectedPoolProgramId: 0,
    selectedPoolYear: 0,
    statisticsLoading: false,
    statistics: undefined,
    historyLoading: false,
    selectionHistory: []
  }

  poolProgramsSubscription = new Subscription()
  poolStatisticSubscription = new Subscription()
  subscriptions = new Subscription()
  company$ = new Subject<number>()

  componentDidMount() {
    this.loadPoolProgramsYear()
  }

  componentWillUnmount() {
    this.poolProgramsSubscription && this.poolProgramsSubscription.unsubscribe()
    this.poolStatisticSubscription && this.poolStatisticSubscription.unsubscribe()
  }

  startLoading() {
    this.setState({
      poolProgramsLoading: true,
      selectedPoolProgramId: 0,
      selectedPoolYear: 0,
      poolProgramsYear: [],
      statisticsLoading: false,
      statistics: undefined,
      historyLoading: false,
      selectionHistory: []
    })
  }

  loadPoolProgramsYear() {
    this.startLoading()
    this.poolProgramsSubscription && this.poolProgramsSubscription.unsubscribe()
    this.poolProgramsSubscription = GetPoolPrograms(this.props.poolId, 5).subscribe(
      (data: PoolProgramYear[]) => {
        const poolProgramId = (data.length > 0) ? data[0].PoolProgramID : 0
        const poolYear = (data.length > 0) ? data[0].Year : 0
        this.setState({
          poolProgramsYear: data,
          selectedPoolProgramId: poolProgramId,
          selectedPoolYear: poolYear,
        })
        this.loadPoolStatistics(poolProgramId)
      },
      (error) => {
        toast.error(error?.errorData?.message || error.message)
      },
      () => {
        this.setState({ poolProgramsLoading: false })

      }
    )
  }

  loadPoolStatistics(poolProgramId: number) {
    this.setState({
      statisticsLoading: true,
      statistics: undefined,
    })

    this.poolStatisticSubscription && this.poolStatisticSubscription.unsubscribe()
    this.poolStatisticSubscription = PoolStatisticsDetailsByProgram(this.props.poolId, poolProgramId).subscribe(
      (data: PoolStatisticsDetails) => {
        this.setState({
          statistics: data
        })
      },
      (error) => {
        toast.error(error?.errorData?.message || error.message)
      },
      () => {
        this.setState({ statisticsLoading: false })

      }
    )
  }

  onPoolProgramSelect(event: React.ChangeEvent<HTMLSelectElement>) {
    if (this.state.poolProgramsYear.length === 0) {
      return
    }
    const poolProgramId = Number(event.target.value)
    const poolYearItem = this.state.poolProgramsYear.find(item => item.PoolProgramID === poolProgramId)
    const poolYear = poolYearItem ? poolYearItem.Year : 0
    this.setState({ selectedPoolProgramId: poolProgramId, selectedPoolYear: poolYear })
    this.loadPoolStatistics(poolProgramId)
  }

  runExcusedReport = () => {
    const params: ExcusedReport = {
      AccountID: 0,//this.state.selectedCompany,
      PoolID: this.props.poolId,
      PoolName: this.props.poolName
    }

    this.subscriptions.add(
      ExcusedReportGenerate(params).subscribe(
        (base64pdf: string) => printPdf(base64pdf),
        (error) => toast.error(FormatError(error))
      )
    )
  }

  render() {
    const { statistics, selectedPoolProgramId, selectedPoolYear, poolProgramsYear, poolProgramsLoading } = this.state
    let labels = ['ESTIMATED TESTS REQUIRED', 'TOTAL TESTS COMPLETED', 'TOTAL TESTS PENDING COLLECTION'];
    let dataDrug = [statistics?.TotalTestsRequiredDrug, statistics?.TotalTestsCompletedDrug, statistics?.TotalTestsPendingDrug]
    let dataAlco = [statistics?.TotalTestsRequiredAlcohol, statistics?.TotalTestsCompletedAlcohol, statistics?.TotalTestsPendingAlcohol]
    const totalTestsRequired = (statistics?.TotalTestsRequiredDrug || 0)  + (statistics?.TotalTestsRequiredAlcohol || 0)
    const totalEstimatedComplete= (statistics?.TotalEstimatedCompleteDrug || 0)  + (statistics?.TotalEstimatedCompleteAlcohol || 0)
    
    const targetDrug = (statistics?.RateOrFixed == 1) ? (statistics?.TargetDrug || 0) : (statistics?.TotalTestsRequiredDrug || 0)
    const targetAlcohol = (statistics?.RateOrFixed == 1) ? (statistics?.TargetAlcohol || 0) : (statistics?.TotalTestsRequiredAlcohol || 0)

    const actualDrug = statistics?.ActualDrug || 0
    const actualAlcohol = statistics?.ActualAlcohol || 0

    return <>
      <Container fluid className="grid-container py-0">
        {!poolProgramsLoading && <Row className="form-group">
          <Col xs={2}>PROGRAM YEAR:</Col>
          {<Col xs={2} md={2}>
            <Form.Control id="pool_program_id"
              as="select"
              disabled={false}
              value={selectedPoolProgramId}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => this.onPoolProgramSelect(e)}>
              {poolProgramsYear.map(item => <option value={item.PoolProgramID} key={"pool_program_" + item.PoolProgramID}>{item.Year}</option>)}
            </Form.Control>
          </Col>}
          <Col xs={4}>PROGRAM START DATE: {"01/01/" + selectedPoolYear}</Col>
          <Col xs={4}>PROGRAM END DATE: {"12/31/" + selectedPoolYear}</Col>
        </Row>}
        <Row>
          <Col>
            {this.state.selectedPoolProgramId > 0 && <SelectionHistoryGrid poolId={this.props.poolId} poolProgramId={this.state.selectedPoolProgramId} poolName={this.props.poolName} companyId={0}/>}
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>TESTING TOTALS</h5>
            <Container fluid>
              <Row>
                <Col xs={4}>PROGRAM YEAR-TO-DATE:</Col>
                <Col xs={2}></Col>
                <Col xs={4}>TOTAL # TESTS REQUIRED:</Col>
                <Col xs={2}>{totalTestsRequired}</Col>
              </Row>
              <Row>
                <Col xs={4}></Col>
                <Col xs={2}></Col>
                <Col xs={4}>TOTAL ESTIMATED COMPLETE:</Col>
                <Col xs={2}>{totalEstimatedComplete}</Col>
              </Row>
              <Row>
                <Col xs={4}></Col>
                <Col xs={2}></Col>
                <Col xs={4}>DIFFERENCE:</Col>
                <Col xs={2}>{totalTestsRequired - totalEstimatedComplete}</Col>
              </Row>

              <Row>
                <Col xs={4}>TARGET DRUG:</Col>
                <Col xs={2}>{targetDrug}{statistics?.RateOrFixed === 1 && <span>%</span>}</Col>
                <Col xs={4}>TARGET ALCOHOL:</Col>
                <Col xs={2}>{targetAlcohol}{statistics?.RateOrFixed === 1 && <span>%</span>}</Col>
              </Row>
              <Row>
                <Col xs={4}>ACTUAL DRUG:</Col>
                <Col xs={2}>{actualDrug}</Col>
                <Col xs={4}>ACTUAL ALCOHOL:</Col>
                <Col xs={2}>{actualAlcohol}</Col>
              </Row>
            </Container>
          </Col>
        </Row>
        {this.state.statisticsLoading && <Row><Col className="text-center"><Spinner animation="border" variant="secondary" /></Col></Row>}
        {this.state.statistics &&
          <Row>
            <Col>
            </Col>
          </Row>}
      </Container>
    </>
  }
}
